import { useEffect } from "react";
import type { GraphqlId } from "@/app/(portfolios)/donors/list/useDonorsLoader";
// import { isRecAutoId } from "@/app/utils/parseRecAutoId";
import { reportError } from "@/app/utils/report";
import type { AiTemperature, ContactMethod, RuleTrigger } from "@/generated/graphql";

/**
 * The primary goal of this module is to centralize analytics code with consistent event names etc.
 * We could have used TS literals or even literal template types, but they're hard to rename and structure.
 *
 * The usage examples are in the function docstrings.
 */
export namespace analytics {
  export function capture(
    eventDetail:
      | [Model, Action, Field?, Location?]
      | [Model, Action, Field?, FieldAction?, Location?]
      | [Model, Action, Location?]
      | [Model, Action, ContactMethod?]
      | [Model, View, Action, Field?, Location?]
      | [Model, View, Action, Field?, FieldAction?, Location?],
    props?: Props,
  ) {
    try {
      const model: Model = eventDetail[0];
      const propsExtended = extendPropsWithModelId(model, props);
      // posthog.capture(eventDetail.join("__"), {
      //   ...propsExtended,
      //   is_rec_auto: propsExtended?.rec_id ? isRecAutoId(propsExtended.rec_id) : false,
      // });
    } catch (error) {
      reportError({ error, isShowFeedbackPopup: false });
    }
  }

  export function captureClick(eventDetail: [Element, ElementId, Location?], props?: Props) {
    try {
      const eventDetailExtended = [Action.clicked, ...eventDetail];
      // posthog.capture(eventDetailExtended.join("__"), props);
    } catch (error) {
      reportError({ error, isShowFeedbackPopup: false });
    }
  }

  export function captureWithGroups(
    eventDetail: [Model, Action, Field?, Location?],
    props?: Props,
    groupEvents: { event: Model; groupCategory?: string }[] = [],
  ) {
    try {
      const model: Model = eventDetail[0];
      const propsExtended = extendPropsWithModelId(model, props);
      // posthog.capture(eventDetail.join("__"), {
      //   ...propsExtended,
      //   is_rec_auto: propsExtended?.rec_id ? isRecAutoId(propsExtended.rec_id) : false,
      // });

      groupEvents.forEach(({ event, groupCategory }) => {
        const groupedEventName = `${event}__any_event`;
        // posthog.capture(groupedEventName, {
        //   ...propsExtended,
        //   group_category: groupCategory,
        //   original_event: eventDetail.join("__"),
        // });
      });
    } catch (error) {
      reportError({ error, isShowFeedbackPopup: false });
    }
  }

  export type Props = Properties & {
    id?: GraphqlId; // instance id of the Model, eg RecManual.id, RecAuto.id, etc
    rec_id?: GraphqlId;
    donor_id?: GraphqlId; // usually the donor affected by the Action
    portfolio_id?: GraphqlId;
    cadence_id?: GraphqlId;
    cadence_template_id?: GraphqlId;
    category?: "call" | "email" | "crm_action" | "letter" | "card"; // use an Enum instead
    rule_trigger?: RuleTrigger;
    crm_url?: string | null;
    is_rec_handled?: boolean; // eg email sent, dismissed, rescheduled, etc
    is_rec_manual_group?: boolean;
    is_template_mailmerge?: boolean;
    is_rule_follow_up?: boolean;

    // potentially redundant props
    template_id?: GraphqlId;
    template_temp?: AiTemperature;
    draft_id?: GraphqlId;

    is_mobile?: boolean;
  };

  /**
   * @param GraphqlId conditional since eg Modals/Drawers are often mounted in an `isOpen = false` state and without an id
   */
  export function useViewedCapture(
    eventDetail: [Model, GraphqlId | null | undefined, Location?],
    props?: Props,
  ) {
    const id = eventDetail[1];

    useEffect(() => {
      try {
        const [model, id, location] = eventDetail;
        if (id) {
          if (location) {
            capture([model, Action.viewed, location], { ...props, id });
          } else {
            capture([model, Action.viewed], { ...props, id });
          }
        }
      } catch (error) {
        reportError({ error, isShowFeedbackPopup: false });
      }
    }, [id]);
  }

  // in those Enums most member value overlaps are ok, since we're using Set to type it
  // but still beware, esp with Location and Field

  export enum Model {
    rec = "rec",
    rec_auto = "rec_auto",
    rec_drafted = "rec_drafted",
    rec_log = "rec_log",
    rec_manual = "rec_manual",
    rec_prospect = "rec_prospect",
    crm_action = "crm_action",
    crm_action_email = "crm_action_email",
    crm_action_email_test = "crm_action_email_test",
    donor = "donor",
    portfolio = "portfolio",
    rule = "rule",
    template = "template",
    linkedin = "linkedin",
    donors_list = "donors_list",
    // Contact report and next steps go together
    contact_report = "contact_report",
    next_step = "next_step",
    cadence = "cadence",
    gift_forecast = "gift_forecast",
    week_in_review = "week_in_review",

    //Dashboard
    reports_actions = "reports_actions",
    reports_impact = "reports_impact",
    reports_actions_or_impact = "reports_actions_or_impact",
  }
  export enum View {
    detail = "detail", // the default, so we don't attach it to posthog events
    list = "list",
  }
  export enum Action {
    // CRUD
    created = "created",
    updated = "updated",
    deleted = "deleted",
    viewed = "viewed",

    // analytics.Element actions (see below)
    clicked = "clicked",
    search = "search",
    sort = "sort",
    filter = "filter",
    submitted = "submitted",
    renamed = "renamed",
    used_in_outreach = "used_in_outreach",
    portfolio_moved = "portfolio_moved",

    // Donor list actions
    donors_list_created_from_checkboxes = "donors_list_created_from_checkboxes",
    donors_added_using_checkboxes = "donors_added_using_checkboxes",
    donors_removed_using_checkboxes = "donors_removed_using_checkboxes",
    donors_added_using_bulk_import = "donors_added_using_bulk_import",
    donors_added_using_search = "donors_added_using_search",
    donors_list_used_in_outreach = "donors_list_used_in_outreach",

    // Next step
    next_step_skipped = "next_step_skipped",

    // Rules
    rule_active = "rule_active",
    rule_inactive = "rule_inactive",

    // Cadences
    cadence_individual_outreach_created = "cadence_individual_outreach_created",
    cadence_attached_to_automation = "cadence_attached_to_automation",
    cadence_removed_from_automation = "cadence_removed_from_automation",

    // Gift forecast
    gift_forecast_set_gift_date = "set_gift_date",
    gift_forecast_set_gift_amount = "set_gift_amount",
    gift_forecast_set_gift_date_or_amount = "set_gift_date_or_amount",
    gift_forecast_edit_gift_date = "edit_gift_date",
    gift_forecast_edit_gift_amount = "edit_gift_amount",
    gift_forecast_edit_gift_date_or_amount = "edit_gift_date_or_amount",

    //Bulk outreach
    rec_drafted_created_group = "created_group",
    rec_drafted_updated_group = "updated_group",

    //Dashboard
    set_start_date = "set_start_date",
    set_end_date = "set_end_date",
    selected_user = "selected_user",
    all_useres_selected = "all_useres_selected",
    selected_segment = "selected_segment",
    all_segments_selected = "all_segments_selected",
    selected_type = "selected_type",
    both_type_selected = "both_type_selected",

    //week in review
    recs_made = "recs_made",
    description_saved = "rec_description_saved",
    rec_viewed = "rec_viewed",
    rec_reviewed = "rec_reviewed",
    rec_reviewed_after_edit = "rec_reviewed_after_edit",

    // todo[refactor](p5) maybe add smth as eg "posted" for stateless HTTP POSTs? eg sending a test email to yourself
  }
  export enum Element {
    button = "button",
    link = "link",
    input = "input",
    select = "select",
  }
  export enum ElementId {
    donor_add = "donor_add",
    donor_search = "donor_search",
    crm_donor_link = "crm_donor_link",
    crm_household_donor_link = "crm_household_donor_link",
    crm_action_link = "crm_action_link",
    crm_gift_link = "crm_gift_link",
    rule_list = "rule_list",
    send_test_email = "send_test_email",
    crm_opportunity_link = "crm_opportunity_link",
    regenerate_gift_forecast = "regenerate_gift_forecast",
  }
  export enum Field {
    donors = "donors", // eg on a RecManual or Portfolio
    scheduled_for_date = "scheduled_for_date",
    name = "name",
    email = "email",
    url = "url",
  }
  export enum FieldAction {
    add = "add",
    remove = "remove",
    change = "change",
  }
  export enum Location {
    inbox = "inbox",
    completed = "completed",
    rec_editor = "rec_editor",
    rec_manual_modal = "rec_manual_modal",
    donor_overview = "donor_overview",
    donor_plan = "donor_plan",
    donor_history = "donor_history",
    donor_move_modal = "donor_move_modal",
    donors_add_modal = "donors_add_modal",
    donor_list = "donor_list",
    pre_screen = "pre_screen", // todo[refactor](p5) prob remove and use eg [ev.rec, ev.viewed, ev.inbox]
    donor_search = "donor_search", // duplicates ElementId.donor_search, but they don't overlap. yet.
    navbar = "navbar",
    template_edit_modal = "template_edit_modal",
    template_create_modal = "template_create_modal",
    linkedin_summary = "linkedin_summary",
  }

  export const events = {
    ...Element,
    ...ElementId,
    ...Model,
    ...View,
    ...Action,
    ...Field,
    ...FieldAction,
    ...Location,
  };

  /**
   * Helps to avoid the boilerplate of setting `id` and the `{model}_id` in the props.
   */
  function extendPropsWithModelId(model: Model, props?: Props) {
    if (!props) {
      return props;
    }

    const propsExtended = { ...props };

    if (propsExtended?.id) {
      switch (model) {
        case Model.rec:
        case Model.rec_auto:
        case Model.rec_prospect:
        case Model.rec_manual: {
          propsExtended.rec_id = propsExtended.id;
          break;
        }
        case Model.donor: {
          propsExtended.donor_id = propsExtended.id;
          break;
        }
        case Model.template: {
          propsExtended.template_id = propsExtended.id;
          break;
        }
        case Model.portfolio: {
          propsExtended.portfolio_id = propsExtended.id;
          break;
        }
        case Model.rule: {
          propsExtended.rule_id = propsExtended.id;
          break;
        }
      }
    }
    return propsExtended;
  }
}

export const ev = analytics.events;
